import { Box, Paper, Stack, Typography } from "@mui/material";
import UnderlineButton from "../../buttons/underlineButton.component";
import InfoToolForm from "./infoToolForm.component";
import { useDispatch, useSelector } from "react-redux";
import PairingToolForm from "./pairingToolForm.component";
import SubmitButton from "../../buttons/submitButton.component";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  createSensorStart,
  resetCreateSensorState,
} from "../../../store/features/customer/sensor/sensorSlice";
import {
  createGatewayStart,
  resetCreateGatewayState,
} from "../../../store/features/customer/gateway/gatewaySlice";
import SuccessDialog from "../../muiDialogs/successDialog.component";
import { dialogMessages } from "../utils";
import CustomAlert from "../../alert/alert.component";
import { fetchSensorTypesStart } from "../../../store/features/customer/sensorTypes/sensorTypesSlice";
import { fetchUpdateFrequenciesStart } from "../../../store/features/customer/updateFrequencies/updateFrequenciesSlice";
import {
  isAssignedFieldReferenceError,
  isDevEuiDuplicateError,
  isGatewayEuiDuplicateError,
} from "../../../validation/schemas/toolsSchema";
import { calculatePolygonCenter } from "../../map/mapUtils";
import { fetchInstrumentsStart } from "../../../store/features/customer/instruments/instrumentsSlice";
import { fetchSensorsStart } from "../../../store/features/customer/instruments/sensorsSlice";
import { getErrorMessage } from "../../../utils/errorUtils";

const buttons = (handleDialogConfirm, handleDialogNavigate) => [
  {
    variant: "outlined",
    label: "Vai a I miei strumenti",
    action: handleDialogConfirm,
  },
  {
    variant: "contained",
    label: "Visualizza strumento",
    action: handleDialogNavigate,
  },
];

const toolTypes = [
  {
    value: "sensor",
    label: "Sensore",
  },
  {
    value: "gateway",
    label: "Gateway",
  },
];

const ToolForm = ({ theme, isMobile, accessToken }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const [toolInfo, setToolInfo] = useState("");
  const [formData, setFormData] = useState("");
  const [pairingFormData, setPairingFormData] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  const fields = useSelector((state) => state.fields?.data?.data);
  const { success, error } = useSelector((state) => state.sensor);
  const { success: gatewaysSuccess, error: gatewaysError } = useSelector(
    (state) => state.gateway
  );
  const sensorId = useSelector((state) => state.sensor?.data?.data?.id);
  const gatewayId = useSelector((state) => state.gateway?.data?.id);
  const sensorTypes = useSelector((state) => state.sensorTypes?.data?.data);
  const updateFrequencies = useSelector(
    (state) => state.updateFrequencies?.data?.data
  );

  const handleToolChange = useCallback((toolData) => {
    setToolInfo(toolData);
  }, []);
  const handleFormDataChange = useCallback((formData) => {
    setFormData(formData);
  }, []);
  const handlePairingFormDataChange = useCallback((pairingFormData) => {
    setPairingFormData(pairingFormData);
  }, []);
  const onSubmit = () => {
    const submitData = {
      ...formData,
      ...(pairingFormData && {
        nickname: pairingFormData.nickname,
        field: pairingFormData.field_id,
        gps_position:
          calculatePolygonCenter(pairingFormData.field_position) || null,
      }),
    };
    if (toolInfo === "gateway") {
      dispatch(
        createGatewayStart({ data: submitData, accessToken: accessToken })
      );
    } else {
      dispatch(
        createSensorStart({ data: submitData, accessToken: accessToken })
      );
    }
  };
  const handleDialogConfirm = () => {
    dispatch(resetCreateSensorState());
    dispatch(resetCreateGatewayState());
    navigate("/tools");
  };
  const handleDialogNavigate = () => {
    if (!createdId) return;

    navigate(`/tools/updateTool/${sensorId || gatewayId}`);
    dispatch(resetCreateSensorState());
    dispatch(resetCreateGatewayState());
  };

  useEffect(() => {
    if (toolInfo) {
      setFormData(null);
      setPairingFormData(null);
      reset();
    }
  }, [toolInfo, reset]);

  useEffect(() => {
    if (success || gatewaysSuccess) {
      dispatch(fetchInstrumentsStart({ accessToken: accessToken }));
      dispatch(fetchSensorsStart({ accessToken: accessToken }));
      setOpenDialog(true);
    }
  }, [success, gatewaysSuccess, dispatch, accessToken]);

  useEffect(() => {
    dispatch(fetchSensorTypesStart({ accessToken }));
    dispatch(fetchUpdateFrequenciesStart({ accessToken }));
  }, [accessToken, dispatch]);

  useEffect(() => {
    if (error && isDevEuiDuplicateError(error)) {
      setError("devEui", {
        type: "manual",
        message: "Dev EUI già esistente",
      });
    } else if (error && isAssignedFieldReferenceError(error)) {
      setError("field", {
        type: "manual",
        message: "Terreno non valido o non associato all'utente",
      });
    }
    if (gatewaysError && isGatewayEuiDuplicateError(gatewaysError)) {
      setError("gatewayEui", {
        type: "manual",
        message: "Gateway EUI già esistente",
      });
    }
  }, [error, setError, gatewaysError]);

  useEffect(() => {
    if ((success && sensorId) || (gatewaysSuccess && gatewayId)) {
      setCreatedId(sensorId || gatewayId);
    }
  }, [success, gatewaysSuccess, sensorId, gatewayId]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      height="100%"
      width="100%"
      display="flex"
      flexDirection="column"
    >
      <Paper
        sx={{
          paddingX: isMobile ? 2 : 20,
          paddingY: isMobile ? 2 : 5,
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
        elevation={0}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography fontWeight="bold" fontSize="14px">
            Info strumento
          </Typography>
          <Typography
            fontSize="12px"
            color={theme.palette.custom.infoTextColor}
          >
            Labore minim esse in dolor consectetur qui. Excepteur sit dolor
            aliqua sunt est. Esse nostrud mollit anim ex officia consectetur. Ea
            ullamco qui amet esse cillum ea minim labore exercitation. Fugiat
            anim ex quis tempor ex ad tempor magna eu amet culpa. Quis minim
            fugiat pariatur incididunt.
          </Typography>
          <Stack
            spacing={1}
            direction={isMobile ? "column" : "row"}
            sx={{ alignItems: "center" }}
          >
            <Typography
              fontSize="12px"
              color={theme.palette.custom.infoTextColor}
            >
              Non trovi il tuo strumento?
            </Typography>
            <UnderlineButton
              label="Richiedi di aggiungerlo"
              onClick={() => navigate(`/toolRequest`)}
            />
          </Stack>
          <InfoToolForm
            toolTypes={toolTypes}
            sensorTypes={sensorTypes}
            frequencyUpdateRanges={updateFrequencies}
            onToolChange={handleToolChange}
            onFormDataChange={handleFormDataChange}
            control={control}
            errors={errors}
          />
          {toolInfo === "sensor" && (
            <PairingToolForm
              fields={fields}
              tool={toolInfo}
              onFormDataChange={handlePairingFormDataChange}
              control={control}
              errors={errors}
              clearErrors={clearErrors}
            />
          )}
        </Box>
      </Paper>
      <SubmitButton label="Salva strumento" disabled={!toolInfo} />
      <SuccessDialog
        open={openDialog}
        onConfirm={handleDialogConfirm}
        buttons={buttons(handleDialogConfirm, handleDialogNavigate)}
        title={dialogMessages.addSensor.title}
        description={dialogMessages.addSensor.description}
      />
      {(error || gatewaysError) && (
        <CustomAlert
          severity="error"
          message={getErrorMessage(error || gatewaysError, {
            fallback: "Errore durante la creazione dello strumento",
          })}
        />
      )}
    </Box>
  );
};

export default ToolForm;
